import { type QueryClient } from '@tanstack/react-query'

import { assessmentProcessQueryKeys } from './assessmentProcessQueryClient.utils'
import { recommendationQueryKeys } from './recommendationQueryClient.utils'

export interface PlanningQueryKeyFactory {
  planning: {
    all: () => readonly ['planning']
    plan: (assessmentId: string) => readonly ['planning', 'plan', string]
    items: (assessmentId: string) => readonly ['planning', 'items', string]
    budget: (assessmentId: string) => readonly ['planning', 'budget', string]
    budgetByFunction: (
      assessmentId: string
    ) => readonly ['planning', 'budget', 'by-function', string]
    budgetMonthly: (assessmentId: string) => readonly ['planning', 'budget', 'monthly', string]
    gantt: (assessmentId: string) => readonly ['planning', 'gantt', string]
    syncPreview: (assessmentId: string) => readonly ['planning', 'sync-preview', string]
  }
}

export const planningQueryKeys: PlanningQueryKeyFactory = {
  planning: {
    all: () => ['planning'] as const,
    plan: (assessmentId) => ['planning', 'plan', assessmentId] as const,
    items: (assessmentId) => ['planning', 'items', assessmentId] as const,
    budget: (assessmentId) => ['planning', 'budget', assessmentId] as const,
    budgetByFunction: (assessmentId) =>
      ['planning', 'budget', 'by-function', assessmentId] as const,
    budgetMonthly: (assessmentId) => ['planning', 'budget', 'monthly', assessmentId] as const,
    gantt: (assessmentId) => ['planning', 'gantt', assessmentId] as const,
    syncPreview: (assessmentId) => ['planning', 'sync-preview', assessmentId] as const
  }
}

export interface PlanningQueryUtils {
  invalidatePlanningQueries: (queryClient: QueryClient, assessmentId: string) => Promise<void>
  invalidateBudgetQueries: (queryClient: QueryClient, assessmentId: string) => Promise<void>
}

export const planningQueryUtils: PlanningQueryUtils = {
  async invalidatePlanningQueries(queryClient, assessmentId) {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: planningQueryKeys.planning.plan(assessmentId)
      }),
      queryClient.invalidateQueries({
        queryKey: planningQueryKeys.planning.items(assessmentId)
      }),
      queryClient.invalidateQueries({
        queryKey: planningQueryKeys.planning.budget(assessmentId)
      }),
      queryClient.invalidateQueries({
        queryKey: planningQueryKeys.planning.budgetByFunction(assessmentId)
      }),
      queryClient.invalidateQueries({
        queryKey: planningQueryKeys.planning.budgetMonthly(assessmentId)
      }),
      queryClient.invalidateQueries({
        queryKey: planningQueryKeys.planning.gantt(assessmentId)
      }),
      queryClient.invalidateQueries({
        queryKey: assessmentProcessQueryKeys.assessmentProcess.tree(assessmentId)
      }),
      queryClient.invalidateQueries({
        queryKey: recommendationQueryKeys.recommendations.byAssessment(assessmentId)
      })
    ])
  },

  async invalidateBudgetQueries(queryClient, assessmentId) {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: planningQueryKeys.planning.budget(assessmentId)
      }),
      queryClient.invalidateQueries({
        queryKey: planningQueryKeys.planning.budgetByFunction(assessmentId)
      }),
      queryClient.invalidateQueries({
        queryKey: planningQueryKeys.planning.budgetMonthly(assessmentId)
      })
    ])
  }
}
